import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { emailValidatedRegex } from '../../../Constants/Constants';
import { usStates } from '../../../utils/Utilities';
import { CompositeCheckBox, CompositeCheckBoxType } from '../../post-fnol-auto/StyledUIElements/CompositeCheckBox';
import {
  CompositeSelectControl,
  CompositeSelectControlInfoType,
} from '../../post-fnol-auto/StyledUIElements/CompositeSelectControl';
import {
  CompositeTextInputField,
  CompositeTextInputFieldType,
} from '../../post-fnol-auto/StyledUIElements/CompositeTextInputField';

export interface ThirdPartyFNOLContactInfoFormType {
  formInfo: ThirdPartyFNOLContactFormInfo;
}

export interface ThirdPartyFNOLContactFormInfo {
  styleInfo: ThirdPartyFNOLContactInfoFormStyleType;
  isFilingAsRepresentative: boolean;
  validYears: { name: string; value: string }[];
  validMakes: { name: string; value: string }[];
  validModels: { name: string; value: string }[];
  isValidMakesLoading: boolean;
  isValidModelsLoading: boolean;
  vehicleYearSelectionChangeHandler: (selectedYear: string) => void;
  vehicleMakeSelectionChangeHandler: (selectedMake: string) => void;
  consolidatedDataUpdateHandler: (info: any) => void;
}

export interface ThirdPartyFNOLContactInfoFormStyleType {
  pageTitleStyle: {
    fontFamily: string;
    fontWeight: number;
    fontSize: string;
    color: string;
    lineHeight: string;
  };
  descriptionStyle: {
    fontFamily: string;
    fontWeight: number;
    fontSize: string;
    color: string;
    lineHeight: string;
  };
  sectionTitleStyle: {
    fontFamily: string;
    fontWeight: number;
    fontSize: string;
    color: string;
    lineHeight: string;
  };
  labelTextStyle: {
    fontFamily: string;
    fontWeight: number;
    fontSize: string;
    color: string;
    lineHeight: string;
    placeHolderColor: string;
    bottomMargin: string;
  };
  textFieldStyle: {
    fontFamily: string;
    fontWeight: number;
    fontSize: string;
    color: string;
    lineHeight: string;
    placeHolderColor: string;
    borderColor: string;
    isPlaceHolderItalic: boolean;
    height: string;
    borderWidth: string;
  };
  optionSelectStyle: {
    fontFamily: string;
    fontWeight: number;
    fontSize: string;
    color: string;
    lineHeight: string;
    placeHolderColor: string;
    isPlaceHolderItalic: boolean;
    borderColor: string;
    borderWidth: string;
    height: string;
    loadingIndicatorTopMargin: string;
  };
  errorLabelStyle: {
    fontFamily: string;
    fontWeight: number;
    fontSize: string;
    color: string;
  };
  checkBoxStyle: {
    fontFamily: string;
    fontWeight: number;
    fontSize: string;
    color: string;
    checkBoxBorderColor: string;
    checkBoxSelectedColor: string;
  };
}

export default function ThirdPartyFNOLContactForm(props: ThirdPartyFNOLContactInfoFormType) {
  const [panel1Expanded, setPanel1Expanded] = useState<boolean>(true);
  const [panel2Expanded, setPanel2Expanded] = useState<boolean>(true);
  // Section 1
  const pageTitle = props.formInfo.isFilingAsRepresentative
    ? 'Please provide contact and vehicle information.'
    : 'Please provide your contact and vehicle information.';
  const sectionTitle1 = 'Contact Info';
  const firstNameFieldText = props.formInfo.isFilingAsRepresentative ? "Your Customer's First Name*" : 'First Name*';
  const lastNameFieldText = props.formInfo.isFilingAsRepresentative ? "Your Customer's Last Name*" : 'Last Name*';
  const customerIsDriverCheckBoxText = props.formInfo.isFilingAsRepresentative
    ? 'The customer was the driver of the vehicle'
    : 'I was the driver of the vehicle';
  const phoneNumberLabelText = props.formInfo.isFilingAsRepresentative
    ? "Your Customer's Phone Number*"
    : 'Phone Number*';
  const emailLabelText = props.formInfo.isFilingAsRepresentative ? "Your Customer's Email Address" : 'Email Address';
  const aVehicleWasInvolvedCheckBoxText = 'A vehicle was involved.';
  const addressLine1LabelText = props.formInfo.isFilingAsRepresentative
    ? "Your Customer's Address Line 1"
    : 'Address Line 1';
  const addressLine2LabelText = props.formInfo.isFilingAsRepresentative
    ? "Your Customer's Address Line 2"
    : 'Address Line 2';

  const cityLabelText = props.formInfo.isFilingAsRepresentative ? "Your Customer's City" : 'City';
  const stateLabelText = props.formInfo.isFilingAsRepresentative ? "Your Customer's State" : 'State';
  const zipLabelText = props.formInfo.isFilingAsRepresentative ? "Your Customer's Zip Code" : 'Zip Code';

  // Section 2
  const sectionTitle2 = 'Insurance Company';
  const insuranceCompanyNameLabelText = props.formInfo.isFilingAsRepresentative
    ? "Insurance Company's Name*"
    : "Your Insurance Company's Name";
  const adjusterFirstNameLabelText = props.formInfo.isFilingAsRepresentative
    ? 'Your First Name*'
    : "Your Adjuster's First Name";
  const adjusterLastNameLabelText = props.formInfo.isFilingAsRepresentative
    ? 'Your Last Name*'
    : "Your Adjuster's Last Name";
  const adjusterPhoneNumberLabelText = props.formInfo.isFilingAsRepresentative
    ? 'Your Phone Number*'
    : "Your Adjuster's Phone Number";
  const adjusterPhoneNumberExtensionLabelText = props.formInfo.isFilingAsRepresentative
    ? 'Your Phone Extension'
    : 'Phone Extension';
  const adjusterEmailLabelText = props.formInfo.isFilingAsRepresentative
    ? 'Your Email Address'
    : "Your Adjuster's Email Address";
  const policyNumberLabelText = props.formInfo.isFilingAsRepresentative
    ? "Customer's Policy Number"
    : 'Your Policy Number';
  const claimNumberLabelText = props.formInfo.isFilingAsRepresentative
    ? "Customer's Claim Number"
    : 'Your Claim Number';
  const vehicleYearLabelText = props.formInfo.isFilingAsRepresentative
    ? "Your Customer's Vehicle Year"
    : 'Vehicle Year';
  const vehicleMakeLabelText = props.formInfo.isFilingAsRepresentative
    ? "Your Customer's Vehicle Make"
    : 'Vehicle Make';
  const vehicleModelLabelText = props.formInfo.isFilingAsRepresentative
    ? "Your Customer's Vehicle Model"
    : 'Vehicle Model';
  // Section 1
  const [customerFirstName, setCustomerFirstName] = useState('');
  const [customerLastName, setCustomerLastName] = useState('');
  const [isCustomerAlsoDriver, setIsCustomerAlsoDriver] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhoneNumber, setCustomerPhoneNumber] = useState('');
  const [isaVehicleInvolved, setIsaVehicleInvolved] = useState(false);
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [zip, setZip] = useState('');
  const [vehicleYear, setVehicleYear] = useState('');
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  // Section 2
  const [insuranceCompanyName, setInsuranceCompanyName] = useState('');
  const [adjusterFirstName, setAdjusterFirstName] = useState('');
  const [adjusterLastName, setAdjusterLastName] = useState('');
  const [adjusterPhoneNumber, setAdjusterPhoneNumber] = useState('');
  const [adjusterPhoneExtensionNumber, setAdjusterPhoneExtensionNumber] = useState('');
  const [adjusterEmail, setAdjusterEmail] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [claimNumber, setClaimNumber] = useState('');

  const { vehicleYearSelectionChangeHandler } = props.formInfo;
  const { vehicleMakeSelectionChangeHandler } = props.formInfo;
  const { consolidatedDataUpdateHandler } = props.formInfo;

  const handleChangeOnPanel1 = () => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setPanel1Expanded(panel1Expanded ? false : true);
  };
  const handleChangeOnPanel2 = () => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setPanel2Expanded(panel2Expanded ? false : true);
  };

  useEffect(() => {
    // Update parent state
    if (vehicleYear.length > 0) {
      vehicleYearSelectionChangeHandler(vehicleYear);
    }
  }, [vehicleYear, vehicleYearSelectionChangeHandler]);

  useEffect(() => {
    // Update parent state
    if (vehicleMake.length > 0) {
      vehicleMakeSelectionChangeHandler(vehicleMake);
    }
  }, [vehicleMake, vehicleMakeSelectionChangeHandler]);

  useEffect(() => {
    // Update parent state
    const consolidatedData = {
      customerFirstName,
      customerLastName,
      isCustomerAlsoDriver,
      customerEmail,
      customerPhoneNumber,
      isaVehicleInvolved,
      addressLine1,
      addressLine2,
      city,
      stateCode,
      zip,
      vehicleYear,
      vehicleMake,
      vehicleModel,
      insuranceCompanyName,
      adjusterFirstName,
      adjusterLastName,
      adjusterPhoneNumber,
      adjusterPhoneExtensionNumber,
      adjusterEmail,
      policyNumber,
      claimNumber,
    };
    if (
      customerFirstName.length &&
      customerLastName.length &&
      customerPhoneNumber.length &&
      !props.formInfo.isFilingAsRepresentative
    ) {
      consolidatedDataUpdateHandler(consolidatedData);
    } else if (
      props.formInfo.isFilingAsRepresentative &&
      customerFirstName.length &&
      customerLastName.length &&
      customerPhoneNumber.length &&
      insuranceCompanyName &&
      adjusterFirstName &&
      adjusterLastName &&
      adjusterPhoneNumber
    ) {
      consolidatedDataUpdateHandler(consolidatedData);
    }
  }, [
    addressLine1,
    addressLine2,
    adjusterEmail,
    adjusterFirstName,
    adjusterLastName,
    adjusterPhoneExtensionNumber,
    adjusterPhoneNumber,
    city,
    claimNumber,
    customerEmail,
    customerFirstName,
    customerFirstName.length,
    customerLastName,
    customerLastName.length,
    customerPhoneNumber,
    customerPhoneNumber.length,
    insuranceCompanyName,
    isCustomerAlsoDriver,
    isaVehicleInvolved,
    policyNumber,
    props.formInfo.isFilingAsRepresentative,
    stateCode,
    vehicleMake,
    vehicleModel,
    vehicleYear,
    zip,
  ]);

  // Section 1
  const firstNameTextChangeHandler = (text: string) => {
    setCustomerFirstName(text);
  };

  const lastNameTextChangeHandler = (text: string) => {
    setCustomerLastName(text);
  };

  const customerIsDriverCheckBoxSelectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isSelected = (event.target as HTMLInputElement).checked;
    if (isSelected) {
      setIsCustomerAlsoDriver(true);
    } else {
      setIsCustomerAlsoDriver(false);
    }
  };

  const aVehicleInvolvedCheckBoxSelectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isSelected = (event.target as HTMLInputElement).checked;
    if (isSelected) {
      setIsaVehicleInvolved(true);
    } else {
      setIsaVehicleInvolved(false);
    }
  };

  const emailTextChangeHandler = (text: string) => {
    setCustomerEmail(text);
  };

  const phoneNumberChangeHandler = (text: string) => {
    setCustomerPhoneNumber(text);
  };

  const addressLine1TextChangeHandler = (text: string) => {
    setAddressLine1(text);
  };

  const addressLine2TextChangeHandler = (text: string) => {
    setAddressLine2(text);
  };

  const cityTextChangeHandler = (text: string) => {
    setCity(text);
  };

  const stateTextChangeHandler = (code: string) => {
    setStateCode(code);
  };

  const zipTextChangeHandler = (text: string) => {
    setZip(text);
  };
  const vehicleYearTextChangeHandler = (text: string) => {
    setVehicleYear(text);
  };
  const vehicleMakeTextChangeHandler = (text: string) => {
    setVehicleMake(text);
  };
  const vehicleModelTextChangeHandler = (text: string) => {
    setVehicleModel(text);
  };

  // Section 2
  const insuranceCompanyTextChangeHandler = (text: string) => {
    setInsuranceCompanyName(text);
  };

  const adjusterFirstNameTextChangeHandler = (text: string) => {
    setAdjusterFirstName(text);
  };

  const adjusterLastNameTextChangeHandler = (text: string) => {
    setAdjusterLastName(text);
  };

  const adjusterPhoneNumberTextChangeHandler = (text: string) => {
    setAdjusterPhoneNumber(text);
  };

  const adjusterPhoneExtensionChangeHandler = (text: string) => {
    setAdjusterPhoneExtensionNumber(text);
  };

  const adjusterEmailChangeHandler = (text: string) => {
    setAdjusterEmail(text);
  };

  const policyNumberChangeHandler = (text: string) => {
    setPolicyNumber(text);
  };

  const claimNumberChangeHandler = (text: string) => {
    setClaimNumber(text);
  };

  const textFieldInfoTemplate: CompositeTextInputFieldType = {
    topLabelText: '',
    placeHolderText: '',
    errorLabelText: 'Required',
    failedValidationText: '',
    isRequiredField: false,
    isValidationNeeded: false,
    isUsedAsPhoneNumberInput: false,
    topLabelStyle: {
      fontFamily: props.formInfo.styleInfo.labelTextStyle.fontFamily,
      fontWeight: props.formInfo.styleInfo.labelTextStyle.fontWeight,
      fontSize: props.formInfo.styleInfo.labelTextStyle.fontSize,
      color: props.formInfo.styleInfo.labelTextStyle.color,
      lineHeight: props.formInfo.styleInfo.labelTextStyle.lineHeight,
      bottomMargin: props.formInfo.styleInfo.labelTextStyle.bottomMargin,
    },
    inputFieldStyle: {
      fontFamily: props.formInfo.styleInfo.labelTextStyle.fontFamily,
      fontWeight: props.formInfo.styleInfo.labelTextStyle.fontWeight,
      fontSize: props.formInfo.styleInfo.labelTextStyle.fontSize,
      fontColor: props.formInfo.styleInfo.labelTextStyle.color,
      isPlaceHolderItalic: false,
      lineHeight: props.formInfo.styleInfo.labelTextStyle.lineHeight,
      placeHolderColor: props.formInfo.styleInfo.textFieldStyle.placeHolderColor,
      borderColor: props.formInfo.styleInfo.textFieldStyle.borderColor,
      borderRadius: '2px',
      height: props.formInfo.styleInfo.textFieldStyle.height,
      borderWidth: props.formInfo.styleInfo.textFieldStyle.borderWidth,
    },
    errorLabelStyle: {
      fontFamily: props.formInfo.styleInfo.errorLabelStyle.fontFamily,
      fontWeight: props.formInfo.styleInfo.errorLabelStyle.fontWeight,
      fontSize: props.formInfo.styleInfo.errorLabelStyle.fontSize,
      color: props.formInfo.styleInfo.errorLabelStyle.color,
      lineHeight: '',
    },
  };

  // Section 1
  const firstNameTextFieldInfo = {
    ...textFieldInfoTemplate,
    topLabelText: firstNameFieldText,
    placeHolderText: 'First Name',
    isRequiredField: true,
    textChangeHandler: firstNameTextChangeHandler,
  };

  const lastNameTextFieldInfo = {
    ...textFieldInfoTemplate,
    topLabelText: lastNameFieldText,
    placeHolderText: 'Last Name',
    isRequiredField: true,
    textChangeHandler: lastNameTextChangeHandler,
  };

  const customerIsDriverCheckBoxInfo: CompositeCheckBoxType = {
    info: {
      description: customerIsDriverCheckBoxText,
      selectionChangeHandler: customerIsDriverCheckBoxSelectionChange,
      checkBoxStyle: {
        checkBoxBorderColor: props.formInfo.styleInfo.checkBoxStyle.checkBoxBorderColor,
        checkBoxSelectedColor: props.formInfo.styleInfo.checkBoxStyle.checkBoxSelectedColor,
      },
      descriptionStyle: props.formInfo.styleInfo.descriptionStyle,
    },
  };

  const emailTextFieldInfo = {
    ...textFieldInfoTemplate,
    topLabelText: emailLabelText,
    placeHolderText: 'Email Address',
    regularExpression: emailValidatedRegex,
    isRequiredField: false,
    isValidationNeeded: true,
    failedValidationText: 'Invalid customerEmail address',
    textChangeHandler: emailTextChangeHandler,
  };

  const phoneNumberTextFieldInfo = {
    ...textFieldInfoTemplate,
    topLabelText: phoneNumberLabelText,
    placeHolderText: 'Phone Number',
    isUsedAsPhoneNumberInput: true,
    isRequiredField: true,
    textChangeHandler: phoneNumberChangeHandler,
  };

  const aVehicleWasInvlovedCheckBoxInfo: CompositeCheckBoxType = {
    info: {
      description: aVehicleWasInvolvedCheckBoxText,
      selectionChangeHandler: aVehicleInvolvedCheckBoxSelectionChange,
      checkBoxStyle: {
        checkBoxBorderColor: props.formInfo.styleInfo.checkBoxStyle.checkBoxBorderColor,
        checkBoxSelectedColor: props.formInfo.styleInfo.checkBoxStyle.checkBoxSelectedColor,
      },
      descriptionStyle: props.formInfo.styleInfo.descriptionStyle,
    },
  };

  const addressLine1TextFieldInfo = {
    ...textFieldInfoTemplate,
    topLabelText: addressLine1LabelText,
    placeHolderText: 'Address Line 1',
    textChangeHandler: addressLine1TextChangeHandler,
  };

  const addressLine2TextFieldInfo = {
    ...textFieldInfoTemplate,
    topLabelText: addressLine2LabelText,
    placeHolderText: 'Address Line 2',
    textChangeHandler: addressLine2TextChangeHandler,
  };

  const cityTextFieldInfo = {
    ...textFieldInfoTemplate,
    topLabelText: cityLabelText,
    placeHolderText: 'City',
    textChangeHandler: cityTextChangeHandler,
  };

  const stateSelectFieldInfo: CompositeSelectControlInfoType = {
    info: {
      topLabelText: stateLabelText,
      disabled: false,
      options: usStates,
      isOptionsLoading: false,
      placeHolderText: 'State',
      topLabelStyle: props.formInfo.styleInfo.labelTextStyle,
      optionStyle: props.formInfo.styleInfo.optionSelectStyle,
      selectionChangeHandler: stateTextChangeHandler,
    },
  };

  const zipTextFieldInfo = {
    ...textFieldInfoTemplate,
    topLabelText: zipLabelText,
    placeHolderText: 'Zip Code',
    textChangeHandler: zipTextChangeHandler,
  };

  const vehicleYearSelectFieldInfo = {
    info: {
      topLabelText: vehicleYearLabelText,
      options: props.formInfo.validYears,
      disabled: false,
      isOptionsLoading: false,
      placeHolderText: 'Year',
      topLabelStyle: props.formInfo.styleInfo.labelTextStyle,
      optionStyle: props.formInfo.styleInfo.optionSelectStyle,
      selectionChangeHandler: vehicleYearTextChangeHandler,
    },
  };

  const vehicleMakeSelectFieldInfo = {
    info: {
      topLabelText: vehicleMakeLabelText,
      options: props.formInfo.validMakes,
      disabled: false,
      isOptionsLoading: props.formInfo.isValidMakesLoading,
      placeHolderText: 'Vehicle Make',
      topLabelStyle: props.formInfo.styleInfo.labelTextStyle,
      optionStyle: props.formInfo.styleInfo.optionSelectStyle,
      selectionChangeHandler: vehicleMakeTextChangeHandler,
    },
  };

  const vehicleYearModelSelectFieldInfo = {
    info: {
      topLabelText: vehicleModelLabelText,
      options: props.formInfo.validModels,
      disabled: false,
      isOptionsLoading: props.formInfo.isValidModelsLoading,
      placeHolderText: 'Vehicle Model',
      topLabelStyle: props.formInfo.styleInfo.labelTextStyle,
      optionStyle: props.formInfo.styleInfo.optionSelectStyle,
      selectionChangeHandler: vehicleModelTextChangeHandler,
    },
  };

  // Section 2
  const insuranceCompanyFieldInfo = {
    ...textFieldInfoTemplate,
    topLabelText: insuranceCompanyNameLabelText,
    placeHolderText: 'Company Name',
    isRequiredField: true,
    textChangeHandler: insuranceCompanyTextChangeHandler,
  };

  const adjusterFirstNameFieldInfo = {
    ...textFieldInfoTemplate,
    topLabelText: adjusterFirstNameLabelText,
    isRequiredField: true,
    placeHolderText: 'First Name',
    textChangeHandler: adjusterFirstNameTextChangeHandler,
  };

  const adjusterLastNameFieldInfo = {
    ...textFieldInfoTemplate,
    topLabelText: adjusterLastNameLabelText,
    isRequiredField: true,
    placeHolderText: 'Last Name',
    textChangeHandler: adjusterLastNameTextChangeHandler,
  };

  const adjusterPhoneNumberFieldInfo = {
    ...textFieldInfoTemplate,
    isRequiredField: true,
    topLabelText: adjusterPhoneNumberLabelText,
    placeHolderText: 'Phone Number',
    isUsedAsPhoneNumberInput: true,
    textChangeHandler: adjusterPhoneNumberTextChangeHandler,
  };

  const adjusterPhoneExtensionFieldInfo = {
    ...textFieldInfoTemplate,
    topLabelText: adjusterPhoneNumberExtensionLabelText,
    placeHolderText: 'Extension',
    textChangeHandler: adjusterPhoneExtensionChangeHandler,
  };

  const adjusterEmailFieldInfo = {
    ...textFieldInfoTemplate,
    topLabelText: adjusterEmailLabelText,
    placeHolderText: 'Email Address',
    regularExpression: emailValidatedRegex,
    isRequiredField: false,
    isValidationNeeded: true,
    failedValidationText: 'Invalid customerEmail address',
    textChangeHandler: adjusterEmailChangeHandler,
  };

  const policyNumberFieldInfo = {
    ...textFieldInfoTemplate,
    topLabelText: policyNumberLabelText,
    placeHolderText: 'Policy Number',
    textChangeHandler: policyNumberChangeHandler,
  };

  const claimNumberFieldInfo = {
    ...textFieldInfoTemplate,
    topLabelText: claimNumberLabelText,
    placeHolderText: 'Claim Number',
    textChangeHandler: claimNumberChangeHandler,
  };

  return (
    <>
      <Typography sx={{ ...props.formInfo.styleInfo.pageTitleStyle, m: '1rem' }}>{pageTitle}</Typography>
      <Accordion expanded={panel1Expanded} onChange={handleChangeOnPanel1()}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{ m: '0px' }}
        >
          <Typography sx={{ ...props.formInfo.styleInfo.sectionTitleStyle }}>{sectionTitle1}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: '0px 16px 16px 16px' }}>
          <Grid container spacing={2}>
            <Grid item container direction={'column'} xs={12} md={6}>
              <CompositeTextInputField info={firstNameTextFieldInfo} />
            </Grid>
            <Grid item container direction={'column'} xs={12} md={6}>
              <CompositeTextInputField info={lastNameTextFieldInfo} />
            </Grid>
            <Grid item xs={12}>
              <CompositeCheckBox info={customerIsDriverCheckBoxInfo.info} />
            </Grid>
            <Grid item container direction={'column'} xs={12} md={6}>
              <CompositeTextInputField info={phoneNumberTextFieldInfo} />
            </Grid>
            <Grid item container direction={'column'} xs={12} md={6}>
              <CompositeTextInputField info={emailTextFieldInfo} />
            </Grid>
            <Grid item xs={12}>
              <CompositeCheckBox info={aVehicleWasInvlovedCheckBoxInfo.info} />
            </Grid>
            {isaVehicleInvolved && (
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} md={4}>
                  <CompositeSelectControl info={vehicleYearSelectFieldInfo.info} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CompositeSelectControl info={vehicleMakeSelectFieldInfo.info} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CompositeSelectControl info={vehicleYearModelSelectFieldInfo.info} />
                </Grid>
              </Grid>
            )}
            <Grid item xs={12}>
              <CompositeTextInputField info={addressLine1TextFieldInfo} />
            </Grid>
            <Grid item xs={12}>
              <CompositeTextInputField info={addressLine2TextFieldInfo} />
            </Grid>
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12} md={4}>
                <CompositeTextInputField info={cityTextFieldInfo} />
              </Grid>
              <Grid item xs={12} md={4}>
                <CompositeSelectControl info={stateSelectFieldInfo.info} />
              </Grid>
              <Grid item xs={12} md={4}>
                <CompositeTextInputField info={zipTextFieldInfo} />
              </Grid>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={panel2Expanded} onChange={handleChangeOnPanel2()}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
          <Typography sx={{ ...props.formInfo.styleInfo.sectionTitleStyle }}>{sectionTitle2}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {props.formInfo.isFilingAsRepresentative && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CompositeTextInputField info={insuranceCompanyFieldInfo} />
              </Grid>
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} md={6}>
                  <CompositeTextInputField info={adjusterFirstNameFieldInfo} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CompositeTextInputField info={adjusterLastNameFieldInfo} />
                </Grid>
              </Grid>
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} md={6}>
                  <CompositeTextInputField info={adjusterPhoneNumberFieldInfo} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CompositeTextInputField info={adjusterPhoneExtensionFieldInfo} />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <CompositeTextInputField info={adjusterEmailFieldInfo} />
              </Grid>
              <Grid item xs={12}>
                <CompositeTextInputField info={policyNumberFieldInfo} />
              </Grid>
              <Grid item xs={12}>
                <CompositeTextInputField info={claimNumberFieldInfo} />
              </Grid>
            </Grid>
          )}
          {!props.formInfo.isFilingAsRepresentative && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CompositeTextInputField info={policyNumberFieldInfo} />
              </Grid>
              <Grid item xs={12}>
                <CompositeTextInputField info={claimNumberFieldInfo} />
              </Grid>
              <Grid item xs={12}>
                <CompositeTextInputField info={insuranceCompanyFieldInfo} />
              </Grid>
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} md={6}>
                  <CompositeTextInputField info={adjusterFirstNameFieldInfo} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CompositeTextInputField info={adjusterLastNameFieldInfo} />
                </Grid>
              </Grid>
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} md={6}>
                  <CompositeTextInputField info={adjusterPhoneNumberFieldInfo} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CompositeTextInputField info={adjusterPhoneExtensionFieldInfo} />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <CompositeTextInputField info={adjusterEmailFieldInfo} />
              </Grid>
            </Grid>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
}
